import { t as createLogger } from "./app.shared-CmP4XLF_.js";

var log = createLogger("related-products");

var DESKTOP_QUERY = "(min-width: 1025px)";

var RECS_CACHE_LIMIT = 16;

var recsCache = new Map;

function cacheGet(key) {
    if (!key || !recsCache.has(key)) return null;
    const value = recsCache.get(key);
    recsCache.delete(key);
    recsCache.set(key, value);
    return value;
}

function cacheSet(key, value) {
    if (!key) return;
    if (recsCache.has(key)) recsCache.delete(key);
    recsCache.set(key, value);
    while (recsCache.size > RECS_CACHE_LIMIT) recsCache.delete(recsCache.keys().next().value);
}

var RelatedProducts = class extends HTMLElement {
    constructor() {
        super();
        this._loaded = false;
        this._observer = null;
        this._hoverBound = false;
        this._currentSource = null;
        this._abortController = null;
        this._mql = null;
        this._handleIntersect = this._handleIntersect.bind(this);
        this._onCardMouseEnter = this._onCardMouseEnter.bind(this);
        this._onCardMouseLeave = this._onCardMouseLeave.bind(this);
        this._onDesktopChange = this._onDesktopChange.bind(this);
    }
    connectedCallback() {
        this.url = this.dataset.url;
        if (!this.url) return;
        this._mql = window.matchMedia(DESKTOP_QUERY);
        this._mql.addEventListener("change", this._onDesktopChange);
        this.overlay = this.querySelector("[data-header-overlay]");
        this.overlayTitle = this.querySelector("[data-overlay-title]");
        this.overlayPrice = this.querySelector("[data-overlay-price]");
        const slider = this.querySelector("[data-slider]");
        if (slider && slider.children.length > 0) {
            this._reveal();
            this._bindHoverHandlers();
        }
        if (this.dataset.lazy === "manual") return;
        if ("IntersectionObserver" in window) {
            this._observer = new IntersectionObserver(this._handleIntersect, {
                rootMargin: "300px 0px"
            });
            this._observer.observe(this);
        } else this._fetchRecommendations();
    }
    disconnectedCallback() {
        this._observer?.disconnect();
        this._unbindHoverHandlers();
        this._mql?.removeEventListener("change", this._onDesktopChange);
        this._abortController?.abort();
    }
    load() {
        this.removeAttribute("data-stale");
        this._fetchRecommendations();
    }
    setSource(productId) {
        productId = String(productId ?? "");
        if (!productId || !this.url) return;
        const url = new URL(this.url, location.origin);
        url.searchParams.set("product_id", productId);
        this.url = url.toString();
        this.dataset.url = this.url;
        if (this._loaded && this._currentSource === productId) return;
        const cached = cacheGet(this._cacheKey());
        if (cached) {
            this._render(cached);
            this._currentSource = productId;
            return;
        }
        this._loaded = false;
        this._abortController?.abort();
        this._fetchRecommendations();
    }
    _cacheKey(url = this.url) {
        try {
            const params = new URL(url, location.origin).searchParams;
            const section = params.get("section_id") || "section";
            const intent = params.get("intent") || "related";
            const id = params.get("product_id");
            return id ? `${section}:${intent}:${id}` : null;
        } catch {
            return null;
        }
    }
    _handleIntersect(entries, observer) {
        if (entries[0]?.isIntersecting) {
            observer.disconnect();
            this._fetchRecommendations();
        }
    }
    async _fetchRecommendations() {
        if (this._loaded) return;
        this._loaded = true;
        const key = this._cacheKey();
        const sourceId = key?.split(":")[2] ?? null;
        const cached = cacheGet(key);
        if (cached) {
            this._render(cached);
            this._currentSource = sourceId;
            log("network", "recommendations served from cache");
            return;
        }
        log("network", "fetching recommendations", {
            url: this.url
        });
        this._abortController = new AbortController;
        try {
            const response = await fetch(this.url, {
                signal: this._abortController.signal
            });
            if (!response.ok) {
                log("errors", "fetch failed", {
                    status: response.status
                });
                this._loaded = false;
                return;
            }
            const text = await response.text();
            const fetched = (new DOMParser).parseFromString(text, "text/html").querySelector("[data-recommendations-root]");
            if (!fetched || !fetched.innerHTML.trim()) {
                log("network", "no recommendations returned");
                return;
            }
            cacheSet(key, fetched.innerHTML);
            this._render(fetched.innerHTML);
            this._currentSource = sourceId;
            log("network", "recommendations injected");
        } catch (error) {
            if (error.name === "AbortError") return;
            log("errors", "fetch failed", {
                error: error
            });
            this._loaded = false;
        }
    }
    _render(html) {
        this._unbindHoverHandlers();
        this.innerHTML = html;
        if (this.dataset.heading) {
            const headingEl = this.querySelector("[data-recs-heading]");
            if (headingEl) headingEl.textContent = this.dataset.heading;
        }
        this.overlay = this.querySelector("[data-header-overlay]");
        this.overlayTitle = this.querySelector("[data-overlay-title]");
        this.overlayPrice = this.querySelector("[data-overlay-price]");
        this._reveal();
        this._bindHoverHandlers();
        this.querySelector("slider-controls")?.dispatchEvent(new CustomEvent("slider-controls:refresh"));
    }
    _reveal() {
        requestAnimationFrame(() => {
            this.classList.remove("opacity-0");
        });
    }
    _bindHoverHandlers() {
        if (this._hoverBound) return;
        if (!this._mql?.matches) return;
        this._hoverBound = true;
        this._cards = this.querySelectorAll("[data-slide]");
        this._cards.forEach(card => {
            card.addEventListener("mouseenter", this._onCardMouseEnter);
            card.addEventListener("mouseleave", this._onCardMouseLeave);
        });
    }
    _unbindHoverHandlers() {
        if (!this._hoverBound) return;
        this._hoverBound = false;
        this._cards?.forEach(card => {
            card.removeEventListener("mouseenter", this._onCardMouseEnter);
            card.removeEventListener("mouseleave", this._onCardMouseLeave);
        });
    }
    _onDesktopChange() {
        if (this._mql.matches) this._bindHoverHandlers(); else {
            this._unbindHoverHandlers();
            if (this.overlay) this.overlay.style.opacity = "0";
        }
    }
    _onCardMouseEnter(event) {
        const card = event.currentTarget;
        const titleEl = card.querySelector("[data-card-title]");
        const priceEl = card.querySelector("[data-card-price]");
        if (this.overlayTitle && titleEl) this.overlayTitle.textContent = titleEl.textContent;
        if (this.overlayPrice && priceEl) this.overlayPrice.innerHTML = priceEl.innerHTML;
        if (this.overlay) this.overlay.style.opacity = "1";
    }
    _onCardMouseLeave() {
        if (this.overlay) this.overlay.style.opacity = "0";
    }
};

var related_products_default = (() => {
    if (customElements.get("related-products")) return;
    customElements.define("related-products", RelatedProducts);
})();

export { RelatedProducts };